import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { isNil } from 'ramda';
import { useHistory } from 'react-router-dom';
import { Provider } from 'react-redux';
import { compose } from 'redux';

import store from '../../store';
import DatePresenter, { SHORT_FORMAT } from '../../presenters/DatePresenter';
import IntegrationItemPresenter from '../../presenters/FastCash/IntegrationItemPresenter';
import FastCashPresenter, {
  FAST_CASH_STATES,
} from '../../presenters/FastCashPresenter';
import appRoutes from '../../routes/appRoutes';
import { Dialog } from '../../components';
import { storesRestHooks } from '../../store/Stores/StoresSlice';
import {
  removePlaidAccount,
  fetchPlaidTransactions,
  updateFastCashState,
} from '../../mutations';
import { specialCashbackRateRestHooks } from '../../store/FastCash/SpecialCashbackRateRepositorySlice';
import { specialCashbackRatesRestHooks } from '../../store/FastCash/SpecialCashbackRatesRepositorySlice';

import Form from './components/Form';
import FastCashContent from './components/FastCashContent';
import FastCashLog from './components/FastCashLog';

const FastCashAdminPanel = (props) => {
  const { relay, fastCash, fastCashLists, searchParams, onSearchParamsChange } =
    props;
  const { byAuth0Id, afterDate, beforeDate } = searchParams;
  const [auth0Id, setAuth0Id] = useState(byAuth0Id);
  const [startDate, setStartDate] = useState(afterDate);
  const [endDate, setEndDate] = useState(beforeDate);
  const [editedItem, setEditedItem] = useState(null);
  const [errors, setErrors] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isBlockDialogOpen, setIsBlockDialogOpen] = useState(false);
  const [isLogModalOpen, setIsLogModalOpen] = useState(false);
  const [isFlagUser, setIsFlagUser] = useState(
    fastCash?.specialCashbackRate?.state === 'active' ? true : false
  );
  const userHasFastCash = !isNil(fastCash);
  const history = useHistory();

  const { createSpecialCashbackRate, deleteSpecialCashbackRate } =
    specialCashbackRateRestHooks.use();

  const {
    loadSpecialCashbackRates,
    specialCashbackRates: { items: specialCashbackRates },
  } = specialCashbackRatesRestHooks.use();

  const handleDialogOpen = () => setIsDialogOpen(true);

  const handleDialogsClose = () => {
    setIsDialogOpen(false);
    setIsBlockDialogOpen(false);
  };

  const handleFlagUser = async ({ cashbackRate, comment }) => {
    setIsFlagUser(true);
    if (!isNil(cashbackRate)) {
      await createSpecialCashbackRate(fastCash?.id, {
        cashbackRate: cashbackRate,
        comment: comment,
      });
      window.location.reload();
    }
  };

  const handleUnFlagUser = async () => {
    setIsFlagUser(false);
    if (!isNil(fastCash?.specialCashbackRate?.cashbackRate)) {
      await deleteSpecialCashbackRate(
        fastCash?.id,
        fastCash?.specialCashbackRate?.id
      );
      window.location.reload();
    }
  };

  const handleBlockDialogOpen = () => setIsBlockDialogOpen(true);

  const handleAuth0IdUpdate = ({ target: { value } }) => {
    setAuth0Id(value);
  };

  const handleStartDateChange = (date) => {
    setStartDate(DatePresenter.shortFormattedDate(date));
  };

  const handleEndDateChange = (date) => {
    setEndDate(DatePresenter.shortFormattedDate(date));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    onSearchParamsChange({
      ...searchParams,
      byAuth0Id: auth0Id,
      afterDate: startDate,
      beforeDate: endDate,
    });
  };

  const handlePlaidDialogOpen = (item) => {
    setEditedItem(item);
    handleDialogOpen();
  };

  const handlePlaidAccountRemove = () => {
    const itemId = IntegrationItemPresenter.id(editedItem);
    return removePlaidAccount(relay.environment, itemId, {
      onCompleted: handleSubmitSuccess('removePlaidAccount'),
    });
  };

  const handleFetchPlaidTransactions = (item) => {
    const integrationItemId = IntegrationItemPresenter.id(item);
    const params = { integrationItemId, startDate, endDate };

    return fetchPlaidTransactions(relay.environment, params, {
      onCompleted: handleFetchComplete('fetchPlaidTransactions'),
    });
  };

  const handleSubmitSuccess = (key) => (response) => {
    const { errorMessage, successMessage } = response[key];
    if (errorMessage) {
      return handleError(errorMessage);
    }
    handleDialogsClose();
    window.alert(successMessage);
  };

  const handleFetchComplete = (key) => (response) => {
    const { errorMessage, successMessage } = response[key];
    if (errorMessage) {
      return handleError(errorMessage);
    }
    window.alert(successMessage);
  };

  const handleError = (errorMessage) => {
    setErrors(errorMessage);
  };

  const handleBlockAccount = () =>
    handleUpdateFastCashState(FAST_CASH_STATES.blocked);

  const handleUnblockAccount = () =>
    handleUpdateFastCashState(FAST_CASH_STATES.active);

  const handleUpdateFastCashState = (fastCashState) => {
    const params = {
      ...searchParams,
      fastCashId: FastCashPresenter.id(fastCash),
      fastCashState: fastCashState,
      afterDate: startDate,
      beforeDate: endDate,
      byAuth0Id: auth0Id,
    };
    return updateFastCashState(relay.environment, params, {
      onCompleted: handleSubmitSuccess('updateFastCashState'),
    });
  };

  const handleLogModalOpen = () => setIsLogModalOpen(true);

  const handleLogModalClose = () => setIsLogModalOpen(false);

  const {
    loadStores,
    stores: { items: stores },
  } = storesRestHooks.use();

  useEffect(() => {
    if (auth0Id && !userHasFastCash) {
      setErrors('User not found or does not have FastCash');
      return;
    }
    loadStores();
  }, []);


  useEffect(() => {
    loadSpecialCashbackRates(fastCash?.id);
  }, [fastCash]);

  useEffect(() => {
    const path = appRoutes.userFastCashPath(searchParams);
    history.push(path);
  }, [searchParams]);

  return (
    <div>
      {isLogModalOpen && (
        <FastCashLog
          onClose={handleLogModalClose}
          fastCashId={FastCashPresenter.id(fastCash)}
        />
      )}
      <div className="transactions">
        <Form
          onSubmit={handleSubmit}
          onFetchTransactions={handleFetchPlaidTransactions}
          onPlaidDialogOpen={handlePlaidDialogOpen}
          onAuth0IdUpdate={handleAuth0IdUpdate}
          onStartDateChange={handleStartDateChange}
          onEndDateChange={handleEndDateChange}
          onBlockDialogOpen={handleBlockDialogOpen}
          onUnblockAccount={handleUnblockAccount}
          onShowLogsClick={handleLogModalOpen}
          errors={errors}
          auth0Id={auth0Id}
          startDate={startDate}
          endDate={endDate}
          fastCash={fastCash}
          isFlagUser={isFlagUser}
          handleFlagUser={handleFlagUser}
          handleUnFlagUser={handleUnFlagUser}
        />
        {fastCash && (
          <FastCashContent
            fastCash={fastCash}
            searchParams={searchParams}
            onSearchParamsChange={onSearchParamsChange}
            onFetchTransactions={handleFetchPlaidTransactions}
            onPlaidDialogOpen={handlePlaidDialogOpen}
            relay={relay}
            lists={fastCashLists}
            stores={stores}
            isFlagUser={isFlagUser}
            handleFlagUser={handleFlagUser}
            handleUnFlagUser={handleUnFlagUser}
            specialCashbackRates={specialCashbackRates}
          />
        )}
      </div>
      <Dialog
        className="deletion"
        isOpen={isDialogOpen}
        message="This will delete the user's Plaid account and transactions without cashbacks. Are you sure you want to proceed?"
        onConfirm={handlePlaidAccountRemove}
        onCancel={handleDialogsClose}
      />
      <Dialog
        className="deletion"
        isOpen={isBlockDialogOpen}
        message="This will blocked the FastCash Account. Are you sure you want to proceed?"
        onConfirm={handleBlockAccount}
        onCancel={handleDialogsClose}
      />
    </div>
  );
};

FastCashAdminPanel.propTypes = {
  relay: PropTypes.shape().isRequired,
  fastCash: FastCashPresenter.shape(),
  fastCashLists: PropTypes.shape(),
  searchParams: PropTypes.shape().isRequired,
  onSearchParamsChange: PropTypes.func.isRequired,
};

FastCashAdminPanel.defaultProps = {
  fastCash: null,
  fastCashLists: null,
};

const addStore = (ProxyComponent) => (props) =>
  (
    <Provider store={store}>
      <ProxyComponent {...props} />
    </Provider>
  );

export default compose(addStore)(FastCashAdminPanel);
